'use-client';
import React, { useEffect } from 'react';
import type { IStylableButtonImperativeActions } from '../../../StylableButton/StylableButton.types';
import StylableButton from '../../../StylableButton/viewer/StylableButton';
import type { ISlideshowButtonProps } from '../../types';
import { classes as stylableButtonClasses } from '../../../StylableButton/viewer/StylableButton.component.st.css';
import { useSlideshowContext } from '../../SlideshowContext';
import { classes, st } from './SlideshowButton.component.st.css';

const SlideshowButton: React.ForwardRefRenderFunction<
  IStylableButtonImperativeActions,
  ISlideshowButtonProps
> = (
  {
    action,
    isDisabled,
    isDisabledByCtx,
    onDisabledChange,
    translations,
    a11y,
    onClick: propsOnClick,
    onFocus: propsOnFocus,
    onBlur: propsOnBlur,
    ...props
  },
  ref,
) => {
  const { onButtonClick, isButtonDisabled, setIsButtonFocused } =
    useSlideshowContext();
  const disabled = isDisabled ?? isDisabledByCtx;

  const onClick = (event: React.MouseEvent) => {
    onButtonClick(action);
    !disabled && propsOnClick?.(event);
  };

  const onFocus = (event: React.FocusEvent) => {
    setIsButtonFocused(true);
    !disabled && propsOnFocus?.(event);
  };

  const onBlur = (event: React.FocusEvent) => {
    setIsButtonFocused(false);
    !disabled && propsOnBlur?.(event);
  };

  const defaultLabel = {
    next: translations.ariaLabelNext,
    prev: translations.ariaLabelPrev,
  };

  useEffect(() => {
    const uxDisabled = isButtonDisabled(action);
    if (uxDisabled !== isDisabledByCtx) {
      onDisabledChange(uxDisabled);
    }
  }, [isDisabledByCtx, isButtonDisabled, action, onDisabledChange]);

  const updatedA11y = {
    ...a11y,
    label: a11y.label || props?.label || defaultLabel[action],
  };

  return (
    <StylableButton
      {...props}
      onClick={onClick}
      ref={ref}
      className={st(
        stylableButtonClasses.root,
        classes.root,
        disabled ? classes.disabled : '',
      )}
      a11y={updatedA11y}
      isDisabled={disabled}
      onFocus={onFocus}
      onBlur={onBlur}
    />
  );
};

export default React.forwardRef(SlideshowButton);
